import isEqual from 'lodash-es/isEqual'
import {createContext, memo, useContext, useMemo, useState} from 'react'

import type {TableHierarchySettings} from '../../../api/view/contracts'
import {DefaultSearchConfig} from '../../../components/filter-bar/search-context'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroupedBy} from '../../../features/grouping/hooks/use-vertical-grouped-by'
import {useSliceBy} from '../../../features/slicing/hooks/use-slice-by'
import {ViewType} from '../../../helpers/view-type'
import {usePaginatedVariablesWithFieldIds} from '../../../hooks/use-paginated-variables-with-field-ids'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {useViews} from '../../../hooks/use-views'
import type {PaginatedMemexItemsQueryVariables} from '../queries/types'
import {useNextPlaceholderQuery} from '../queries/use-next-placeholder-query'

type PaginatedViewVariablesContextType = {
  variables: PaginatedMemexItemsQueryVariables
}

const PaginatedViewVariablesContext = createContext<PaginatedViewVariablesContextType | null>(null)

export const PaginatedViewVariablesProvider = memo(function PaginatedViewVariablesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const nextVariables = usePaginatedViewQueryVariables()
  const currentViewId = useViews().currentView?.id ?? -1

  const [currentVariablesById, setCurrentVariablesById] = useState<Record<number, PaginatedMemexItemsQueryVariables>>(
    () => ({[currentViewId]: nextVariables}),
  )

  const {setUpNextPlaceholderQueries} = useNextPlaceholderQuery(nextVariables)

  if (!isEqual(currentVariablesById[currentViewId], nextVariables)) {
    // Optionally seed placeholder data to reduce layout shifts while fetching fresh data.
    // If no placeholder data is seeded, the user will see a loading screen every time
    // the variables for a view change.
    if (shouldSeedPlaceholder(nextVariables, currentVariablesById[currentViewId])) {
      setUpNextPlaceholderQueries(currentVariablesById[currentViewId])
    }
    setCurrentVariablesById(prev => ({...prev, [currentViewId]: nextVariables}))
  }

  const activeVariables = currentVariablesById[currentViewId] ?? nextVariables
  const value = useMemo(() => ({variables: activeVariables}), [activeVariables])

  return <PaginatedViewVariablesContext.Provider value={value}>{children}</PaginatedViewVariablesContext.Provider>
})

export const usePaginatedViewVariablesContext = () => useContext(PaginatedViewVariablesContext)

/**
 *
 * Compares the next variables with the current variables to determine if we should seed placeholder
 * data to display while fresh data is fetched.
 *
 * @param nextVariables
 * @param currentVariables
 */
const shouldSeedPlaceholder = (
  nextVariables: PaginatedMemexItemsQueryVariables,
  currentVariables?: PaginatedMemexItemsQueryVariables,
) => {
  if (!currentVariables) return false

  if (!isEqual(nextVariables.fieldIds, currentVariables.fieldIds)) return true
  return false
}

function usePaginatedViewQueryVariables(): PaginatedMemexItemsQueryVariables {
  const {currentView} = useViews()
  const q = DefaultSearchConfig.getSearchQueryFromView(currentView)
  const {viewType, aggregationSettings, layoutSettings} = currentView?.localViewStateDeserialized ?? {}
  const {sorts} = useSortedBy()
  const sortedBy = useMemo(
    () => sorts.map(sort => ({columnId: sort.column.databaseId, direction: sort.direction})),
    [sorts],
  )
  const {groupedByColumnId: horizontalGroupedByColumnId} = useHorizontalGroupedBy()
  let {groupedByColumnId: verticalGroupedByColumnId} = useVerticalGroupedBy()

  // only board views use vertical grouping, but it's always defaulted for any view type
  if (viewType !== ViewType.Board) {
    verticalGroupedByColumnId = undefined
  }

  const {sliceField, sliceValue} = useSliceBy()
  const sliceByColumnId = sliceField?.id

  const sumFields = useMemo(() => {
    return aggregationSettings?.sum.map(f => f.id)
  }, [aggregationSettings?.sum])

  let hierarchy: TableHierarchySettings | undefined
  if (viewType === ViewType.Table) {
    hierarchy = layoutSettings?.table?.hierarchy
  }

  const variablesWithoutFieldIds: PaginatedMemexItemsQueryVariables = useMemo(
    () => ({
      q,
      sortedBy,
      viewType,
      horizontalGroupedByColumnId,
      verticalGroupedByColumnId,
      sliceByColumnId,
      // sliceValue can be `null`, which we want to coerce to undefined
      sliceByValue: sliceValue ?? undefined,
      sumFields,
      hierarchy,
    }),
    [
      q,
      sortedBy,
      viewType,
      horizontalGroupedByColumnId,
      verticalGroupedByColumnId,
      sliceByColumnId,
      sliceValue,
      sumFields,
      hierarchy,
    ],
  )

  return usePaginatedVariablesWithFieldIds(variablesWithoutFieldIds)
}

try{ PaginatedViewVariablesContext.displayName ||= 'PaginatedViewVariablesContext' } catch {}
try{ PaginatedViewVariablesProvider.displayName ||= 'PaginatedViewVariablesProvider' } catch {}